import { MapsAPILoader, AgmInfoWindow } from "@agm/core";
import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { each, result } from "lodash";
import { combineLatest, Subject } from "rxjs";
import { map, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { AllCitiesRequested, AllCountriesRequested, CityModel, CountryModel, selectAllCities, selectAllCountries } from "src/app/core/admin";
import { AssetClassResidentialsService } from "src/app/core/comparable";
import { MapsService } from "src/app/core/map/maps.service";
import { getGeocoderResult } from "src/app/core/v2/map/geocoder";
import { zoomForViewport } from "src/app/core/v2/map/viewport-to-zoom";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { CoordinatePoint } from "src/app/core/v3/coordinate-points";
import { AdministrativeArea, InspectionLocationInfo, Locality, parseDMS, Street, toDMS } from "src/app/core/v3/location";
import { CountryData } from "src/app/views/pages/admin-management/countries/countries-list/local-data";
import { environment } from 'src/environments/environment';
import { SubdomainService } from 'src/app/core/_base/subdomain.service';
import { awConst } from 'src/app/app.constants';

interface State {
    centerLatitude: number | null
    centerLongitude: number | null,
    marker: {
        latitude: number,
        longitude: number
    } | null
    zoom: number,
    countries: CountryModel[],
    cities: CityModel[],
    selectedBuildingId: number | null,
    propertySubTypeId: number | null
}

@Component({
    selector: 'app-inspection-location',
    templateUrl: './inspection-location.component.html',
    styleUrls: ['./inspection-location.component.scss'],
    providers: [ComponentStore]
})
export class InspectionLocationComponent implements OnDestroy {
    /** ViewChild */
    @ViewChild('searchByLocation')
    searchByLocationElementRef!: ElementRef<HTMLInputElement>

    /** Form */
    formGroup = new FormGroup({
        latitude: new FormControl<number | null>(null, [Validators.required]),
        longitude: new FormControl<number | null>(null, [Validators.required]),
        unit: new FormControl<string | null>(null),
        building_name: new FormControl<string | null>(null),
        street_form_array: new FormArray<FormControl<string | null>>([]),
        locality_form_array: new FormArray<FormControl<string | null>>([]),
        city_id: new FormControl<number | null>({ value: null, disabled: true }, [Validators.required]),
        administrative_area_form_array: new FormArray<FormControl<string | null>>([]),
        zip_code: new FormControl<string | null>(null),
        country_id: new FormControl<number | null>({ value: null, disabled: true }, [Validators.required]),
        distance_to_the_sea: new FormControl<string | null>(null),
        elevation_from_sea: new FormControl<string | null>(null),
        location_surrounding: new FormControl<string|null>(null)
    })
    gpsSearchControl = new FormControl<string | null>(null)
    useAsAddressControl = new FormControl<boolean>(true)
    showExistingBuildingControl = new FormControl<boolean>(false)

    /** Private State */
    private _map: google.maps.Map | null = null
    private _mapEventListeners: google.maps.MapsEventListener[] = []
    private _geoCoder = new google.maps.Geocoder()
    private _streetIds: (number | null)[] = []
    private _localityIds: (number | null)[] = []
    private _administrativeAreaIds: (number | null)[] = []
    private _localCountries = CountryData.countries
    private _searchedPlace: google.maps.places.PlaceResult | null = null
    private _infoWindow: AgmInfoWindow | null = null
    private _stateLatitude$ = this._store.select(state => state.centerLatitude)
    private _stateLongitude$ = this._store.select(state => state.centerLongitude)
    private _zoomLevel$ = this._store.select(state => state.zoom)
    private _marker$ = this._store.select(state => state.marker)
    private _centerLatitude$ = combineLatest([
        this._stateLatitude$,
        this._mapsService.latitude$
    ]).pipe(
        map(([incoming, def]) => {
            if (incoming === null) return Number(def);
            return incoming
        })
    )
    private _centerLongitude$ = combineLatest([
        this._stateLongitude$,
        this._mapsService.longitude$
    ]).pipe(
        map(([incoming, def]) => {
            if (incoming === null) return Number(def);
            return incoming
        })
    )
    private _centerPoint$ = combineLatest([this._centerLatitude$, this._centerLongitude$]).pipe(
        map(([lat, lng]) => ({ lat, lng }))
    )
    private _cities$ = this._store.select(state => state.cities)
    private _countries$ = this._store.select(state => state.countries)

    /** Public State */
    mapViewModel$ = combineLatest([
        this._centerPoint$,
        this._zoomLevel$,
        this._marker$
    ]).pipe(
        map(([center, zoom, marker]) => ({ center, zoom, marker }))
    )
    propertySubTypeId$ = this._store.select(state => state.propertySubTypeId)
    cities$ = this._cities$
    countries$ = this._countries$
    buildings$ = combineLatest([
        this._fetchBuildings(),
        this._store.select(state => state.selectedBuildingId)
    ]).pipe(
        map(([buildings, buildingId]) => {
            return buildings.map((b: any) => {
                return {
                    ...b,
                    label: {
                        color: '#000000',
                        fontFamily: '',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        className: `bg-marker bg-marker${b.id == buildingId ? '-selected' : ''}`,
                        text: `B${b.id}`
                    }
                }
            })
        })
    )
    icon = {
        url: './assets/media/icons/my_marker.svg',
    };
    basePath: string = environment.baseApiUrl + `api/${this.subDomainService.subDomain}/files/download?path=`;
    errorFields: string[] = []

    /** Inputs */
    @Input() isSavedBefore: boolean = false
    @Input()
    set centerLatitude(value: number | null) {
        this._store.patchState({ centerLatitude: value })
    }

    @Input()
    set centerLongitude(value: number | null) {
        this._store.patchState({ centerLongitude: value })
    }

    @Input()
    set selectedBuildingId(value: number | null) {
        this._store.patchState({ selectedBuildingId: value })
    }

    @Input()
    set propertySubTypeId(value: number | null) {
        this._store.patchState({ propertySubTypeId: value })
        if (value == 5 || value == 1 || value == 3 || value == 2) {
            this.showExistingBuildingControl.patchValue(true)
        }
    }

    @Input()
    set locationInfo(value: InspectionLocationInfo | null) {
        if (!value) {
            this._setStreetFormArray([])
            this._setLocalityFormArray([])
            this._setAdministrativeFormArray([])
            return;
        }
        this.formGroup.patchValue({
            city_id: value.city_id,
            country_id: value.country_id,
            zip_code: value.zip_code,
            unit: value.unit,
            building_name: value.building_name,
            distance_to_the_sea: value.distance_to_the_sea,
            elevation_from_sea: value.elevation_from_sea,
            location_surrounding: value.location_surrounding
        })
        this._setStreetFormArray(value.streets)
        this._setLocalityFormArray(value.localities)
        this._setAdministrativeFormArray(value.administrative_areas)

        if (value.latitude && value.longitude) {
            this._setMarker({ latitude: value.latitude, longitude: value.longitude })
        }

        this._updateFormState()
    }

    /** Outputs */
    @Output() centerChange = new EventEmitter<CoordinatePoint>()
    @Output() markerChange = new EventEmitter<CoordinatePoint>()
    @Output() buildingSelected: EventEmitter<any> = new EventEmitter()
    @Output() formStateChange = new EventEmitter<{ invalid: boolean | undefined, fields: number }>()

    /** Misc */
    private _onDestroy$ = new Subject<void>()
    constructor(
        private _store: ComponentStore<State>,
        private _mapsService: MapsService,
        private _ngZone: NgZone,
        private _mapsAPILoader: MapsAPILoader,
        private _ngStore: Store,
        private _buildingService: AssetClassResidentialsService,
        private subDomainService: SubdomainService
    ) {
        this._store.setState({
            centerLatitude: null,
            centerLongitude: null,
            zoom: 15,
            marker: null,
            countries: [],
            cities: [],
            selectedBuildingId: null,
            propertySubTypeId: null
        })

        this._fetchLinkedTables()

        formControlValueChanges(this.gpsSearchControl).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(input => {
            const parsedLocation = parseDMS(input)
            if (!parsedLocation) return;
            this.centerChange.emit({
                latitude: parsedLocation.lat,
                longitude: parsedLocation.lng
            })

            if (this.useAsAddressControl.value) {
                this._onRightClick(new google.maps.LatLng(parsedLocation.lat, parsedLocation.lng))
            }
        })

        formControlValueChanges(this.useAsAddressControl).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(value => {
            if (!value) return;
            if (!this._searchedPlace) return;
            if (!this._searchedPlace.geometry) return;
            this._onRightClick(this._searchedPlace.geometry.location)
        })
    }

    ngOnDestroy(): void {
        this._mapEventListeners.forEach(listener => listener.remove())
        this._onDestroy$.next()
        this._onDestroy$.complete()
    }

    onMapReady(event: google.maps.Map) {
        this._map = event
        const dragendListener = this._map.addListener('dragend', () => {
            this._ngZone.run(() => {
                const center = this._map?.getCenter()
                if (center) {
                    this.centerChange.emit({ latitude: center.lat(), longitude: center.lng() })
                }
            })
        })
        this._mapEventListeners.push(dragendListener)
        const clickListener = this._map.addListener('rightclick', (e: google.maps.MouseEvent) => {
            this._ngZone.run(() => {
                this._onRightClick(e.latLng)
            })
        })
        this._mapEventListeners.push(clickListener)

        this._mapsAPILoader.load().then(() => {
            if (this.searchByLocationElementRef) {
                const autoComplete = new google.maps.places.Autocomplete(this.searchByLocationElementRef.nativeElement, {
                    types: ['geocode']
                })
                const placeChangedListener = autoComplete.addListener('place_changed', () => {
                    this._ngZone.run(() => {
                        const place = autoComplete.getPlace()
                        this._searchedPlace = place
                        if (place.geometry === undefined || place.geometry === null) {
                            return;
                        }
                        this._store.patchState({
                            zoom: zoomForViewport(place.geometry.viewport, 12)
                        })
                        this.centerChange.emit({ latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng() })
                        const locationDms = `${toDMS(place.geometry.location.lat(), 'lat')} ${toDMS(place.geometry.location.lng(), 'lng')}`
                        this.gpsSearchControl.patchValue(locationDms, { emitEvent: false })

                        if (this.useAsAddressControl.value) {
                            this._onRightClick(place.geometry.location)
                        }
                    })
                })
                this._mapEventListeners.push(placeChangedListener)
            }
        })
    }

    onAddStreet() {
        this._addStreet({ id: null, value: null })
    }

    onRemoveStreet(index: number) {
        this.formGroup.controls.street_form_array.removeAt(index)
    }

    onAddLocality() {
        this._addLocality({ id: null, value: null })
    }

    onRemoveLocality(index: number) {
        this.formGroup.controls.locality_form_array.removeAt(index)
    }

    onAddAdministrativeArea() {
        this._addAdministrativeArea({ id: null, value: null })
    }

    onRemoveAdministrativeArea(index: number) {
        this.formGroup.controls.administrative_area_form_array.removeAt(index)
    }

    onShowInfo(building: any, info: AgmInfoWindow) {
        this._infoWindow = info;
        this._infoWindow.open()

        this.centerChange.emit({
            latitude: Number(building.locationData.latitude) + 0.003,
            longitude: Number(building.locationData.longitude)
        })
    }

    onImgError(event: any) {
        event.target.src = awConst.IMAGE_NOT_FOUND
    }

    onSelectBuilding(building: any) {
        this.buildingSelected.emit(building)
        this._store.patchState({
            selectedBuildingId: building.id
        })
        if (this._infoWindow) {
            this._infoWindow.close()
        }
        this._setMarker({
            latitude: Number(building.locationData.latitude),
            longitude: Number(building.locationData.longitude)
        })
    }

    validate(): boolean {
        let errorFields: string[] = []
        let hasError = false
        if (this.formGroup.invalid) {
            Object.keys(this.formGroup.controls).forEach(controlName => {
                if (this.formGroup.get(controlName)?.invalid && controlName !== 'latitude') {
                    console.log(controlName)
                    errorFields = [...errorFields, controlName]
                }
                this.formGroup.get(controlName)?.markAsTouched()
            })
            this.errorFields = errorFields
            hasError = true
        }
        return hasError
    }

    getData(): InspectionLocationInfo {
        return {
            longitude: this.formGroup.controls.longitude.value,
            latitude: this.formGroup.controls.latitude.value,
            city_id: this.formGroup.controls.city_id.value,
            country_id: this.formGroup.controls.country_id.value,
            unit: this.formGroup.controls.unit.value,
            building_name: this.formGroup.controls.building_name.value,
            zip_code: this.formGroup.controls.zip_code.value,
            streets: this._getStreetValues(),
            localities: this._getLocalityValues(),
            administrative_areas: this._getAdministrativeAreaValues(),
            distance_to_the_sea: this.formGroup.controls.distance_to_the_sea.value,
            elevation_from_sea: this.formGroup.controls.elevation_from_sea.value,
            location_surrounding: this.formGroup.controls.location_surrounding.value
        }
    }

    private _fetchLinkedTables() {
        this._ngStore.dispatch(new AllCountriesRequested())
        this._ngStore.dispatch(new AllCitiesRequested())
        this._ngStore.select(selectAllCountries).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(entities => {
            const countries: CountryModel[] = []
            each(entities, (entity) => {
                countries.push(entity)
            })
            this._store.patchState({ countries })
        })
        this._ngStore.select(selectAllCities).pipe(
            takeUntil(this._onDestroy$)
        ).subscribe(entities => {
            this._store.patchState({ cities: entities })
        })
    }

    private _onRightClick(event: google.maps.LatLng) {
        this._getGeocodeResult(event)
        const latitude = event.lat()
        const longitude = event.lng()
        this._setMarker({ latitude, longitude })
    }

    private _setMarker(event: CoordinatePoint) {
        const latitude = event.latitude
        const longitude = event.longitude
        this._store.patchState({
            marker: {
                latitude: latitude,
                longitude: longitude
            }
        })
        this.formGroup.patchValue({
            latitude: latitude,
            longitude: longitude
        })
        this.markerChange.emit({
            latitude,
            longitude
        })
    }

    private _setStreetFormArray(items: Street[]) {
        this.formGroup.controls.street_form_array.clear()
        this._streetIds = []
        const _items: Street[] = items.length == 0
            ? [{ id: null, value: null }]
            : [...items]
        _items.forEach(item => this._addStreet(item))
    }

    private _getStreetValues(): Street[] {
        const values: Street[] = []
        this.formGroup.controls.street_form_array.controls.forEach((control, i) => {
            const _item: Street = { id: null, value: null }
            if (this._streetIds[i]) {
                _item.id = this._streetIds[i]
            }
            _item.value = control.value
            values.push(_item)
        })
        return values
    }

    private _addStreet(item: Street) {
        this.formGroup.controls.street_form_array.push(new FormControl<string | null>(item.value))
        this._streetIds.push(item.id)
    }

    private _setLocalityFormArray(items: Locality[]) {
        this.formGroup.controls.locality_form_array.clear()
        this._localityIds = []
        const _items: Locality[] = items.length == 0
            ? [{ id: null, value: null }]
            : [...items]
        _items.forEach(item => this._addLocality(item))
    }

    private _getLocalityValues(): Locality[] {
        const values: Locality[] = []
        this.formGroup.controls.locality_form_array.controls.forEach((control, i) => {
            const _item: Locality = { id: null, value: null }
            if (this._localityIds[i]) {
                _item.id = this._localityIds[i]
            }
            _item.value = control.value
            values.push(_item)
        })
        return values
    }

    private _addLocality(item: Locality) {
        this.formGroup.controls.locality_form_array.push(new FormControl<string | null>(item.value))
        this._localityIds.push(item.id)
    }

    private _setAdministrativeFormArray(items: AdministrativeArea[]) {
        this.formGroup.controls.administrative_area_form_array.clear()
        this._administrativeAreaIds = []
        const _items: AdministrativeArea[] = items.length == 0
            ? [{ id: null, value: null }]
            : [...items]
        _items.forEach(item => this._addAdministrativeArea(item))
    }

    private _getAdministrativeAreaValues(): AdministrativeArea[] {
        const values: AdministrativeArea[] = []
        this.formGroup.controls.administrative_area_form_array.controls.forEach((control, i) => {
            const _item: AdministrativeArea = { id: null, value: null }
            if (this._administrativeAreaIds[i]) {
                _item.id = this._administrativeAreaIds[i]
            }
            _item.value = control.value
            values.push(_item)
        })
        return values
    }

    private _addAdministrativeArea(item: Locality) {
        this.formGroup.controls.administrative_area_form_array.push(new FormControl<string | null>(item.value))
        this._administrativeAreaIds.push(item.id)
    }

    private _getGeocodeResult(event: google.maps.LatLng) {
        this._geoCoder.geocode({
            location: event
        }, (results, status) => {
            if (status && results && results.length > 1) {
                const result = getGeocoderResult(results)
                if (result == null) return;
                this.formGroup.patchValue({
                    zip_code: result.zipcode,
                    building_name: result.buildingName,
                    street_form_array: [result.street],
                    locality_form_array: [result.locality],
                    administrative_area_form_array: [result.adminRegion]
                })
                this._setCountryAndCity({ countryCode: result.countryCode, cityName: result.city })
            }
        })
    }

    private _setCountryAndCity = this._store.effect<{ countryCode: string|null, cityName: string|null }>(result$ =>
        result$.pipe(
            withLatestFrom(this._store.state$),
            tap(([result, state]) => {
                if (state.countries.length === 0) return;
                const country = this._localCountries.find(c => c.countryCode === result.countryCode)
                if (country === undefined) return;
                const _country = state.countries.find(el => el.name.toLocaleLowerCase() === country.countryName.toLocaleLowerCase())
                const _cities = state.cities.filter(el => el.name.toLocaleLowerCase() === result.cityName?.toLocaleLowerCase())

                if (!_country) return;
                const _city = _cities.find(item => item.country_id === _country.id)
                if (!_city) return;
                this.formGroup.patchValue({
                    country_id: _country.id,
                    city_id: _city.id
                })
            })
        )
    )

    private _fetchBuildings() {
        return this._buildingService.getBuildingInfos().pipe(
            map(res => res.data),
            map(buildings => {
                return buildings.map((b: any) => {
                    return {
                        ...b,
                        total_floors: b.internalAspectData
                            ? (Number(b.internalAspectData.above_floors) + Number(b.internalAspectData.below_floors))
                            : null,
                    }
                })
            })
        )
    }

    private _updateFormState() {
        let _state: boolean | undefined = undefined
        let _fields = 0
        const controlNames = ['country_id', 'city_id']
        controlNames.forEach(controlName => {
            const result = this._updateStateAndFields(controlName, _fields)
            _state = result.state
            _fields = result.fields
        })
        if (this.isSavedBefore) {
            this.formStateChange.next({ invalid: _state, fields: _fields })
        } else if (_state === false) {
            this.formStateChange.next({ invalid: _state, fields: 0 })
        }
    }

    private _updateStateAndFields(controlName: string, fields: number): { state: boolean | undefined, fields: number } {
        if (!this.formGroup.get(controlName)?.invalid) {
            return { state: false, fields: 0 }
        }
        return { state: true, fields: fields + 1 }
    }
}